import { supabase, Subscription, UserPreferences } from '../lib/supabase';
import { sendRenewalReminder, sendMultipleRenewalsReminder } from './emailService';

export interface ReminderData {
  userId: string;
  userEmail: string;
  userName?: string;
  preferences: UserPreferences;
  subscriptions: Subscription[];
}

/**
 * Get all users with their preferences and active subscriptions
 */
export const getAllUsersWithSubscriptions = async (): Promise<ReminderData[]> => {
  try {
    // Get all users with preferences
    const { data: preferences, error: prefError } = await supabase
      .from('user_preferences')
      .select('*')
      .eq('email_notifications', true);

    if (prefError) throw prefError;

    if (!preferences || preferences.length === 0) {
      console.log('No users with email notifications enabled');
      return [];
    }

    // Get active subscriptions for each user
    const usersData: ReminderData[] = [];
    
    for (const pref of preferences) {
      const { data: subscriptions, error: subError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', pref.user_id)
        .eq('is_active', true);

      if (subError) {
        console.error(`Error fetching subscriptions for user ${pref.user_id}:`, subError);
        continue;
      }

      if (subscriptions && subscriptions.length > 0) {
        // Get user email from auth.users
        const { data: userData, error: userError } = await supabase.auth.admin.getUserById(pref.user_id);
        
        if (!userError && userData.user) {
          usersData.push({
            userId: pref.user_id,
            userEmail: userData.user.email || '',
            userName: userData.user.user_metadata?.full_name || userData.user.user_metadata?.name,
            preferences: pref,
            subscriptions: subscriptions,
          });
        }
      }
    }

    return usersData;
  } catch (error) {
    console.error('Error getting users with subscriptions:', error);
    return [];
  }
};

/**
 * Check if a subscription needs a reminder today based on user preferences
 */
export const shouldSendReminderToday = (
  subscription: Subscription,
  reminderDaysBefore: number
): boolean => {
  const today = new Date();
  const renewalDate = new Date(subscription.next_renewal_date);
  const reminderDate = new Date(renewalDate);
  reminderDate.setDate(renewalDate.getDate() - reminderDaysBefore);

  // Check if today is the reminder date
  return (
    today.getFullYear() === reminderDate.getFullYear() &&
    today.getMonth() === reminderDate.getMonth() &&
    today.getDate() === reminderDate.getDate()
  );
};

/**
 * Get subscriptions that need reminders today for a specific user
 */
export const getSubscriptionsNeedingReminders = (
  subscriptions: Subscription[],
  reminderDaysBefore: number
): Subscription[] => {
  return subscriptions.filter(subscription => 
    shouldSendReminderToday(subscription, reminderDaysBefore)
  );
};

/**
 * Send reminders for a single user
 */
export const sendUserReminders = async (userData: ReminderData): Promise<boolean> => {
  try {
    const { preferences, subscriptions, userEmail, userName } = userData;
    
    if (!preferences.email_notifications) {
      console.log(`Email notifications disabled for user ${userData.userId}`);
      return true;
    }

    const subscriptionsNeedingReminders = getSubscriptionsNeedingReminders(
      subscriptions,
      preferences.reminder_days_before
    );

    if (subscriptionsNeedingReminders.length === 0) {
      console.log(`No reminders needed for user ${userData.userId}`);
      return true;
    }

    console.log(`Sending ${subscriptionsNeedingReminders.length} reminder(s) to ${userEmail}`);

    if (subscriptionsNeedingReminders.length === 1) {
      // Send single subscription reminder
      const success = await sendRenewalReminder({
        subscription: subscriptionsNeedingReminders[0],
        daysUntilRenewal: preferences.reminder_days_before,
        userEmail,
        userName,
      });

      if (success) {
        console.log(`✅ Single reminder sent to ${userEmail} for ${subscriptionsNeedingReminders[0].name}`);
      }
      return success;
    } else {
      // Send multiple subscriptions reminder
      const success = await sendMultipleRenewalsReminder({
        subscriptions: subscriptionsNeedingReminders,
        daysUntilRenewal: preferences.reminder_days_before,
        userEmail,
        userName,
      });

      if (success) {
        console.log(`✅ Multiple reminders sent to ${userEmail} for ${subscriptionsNeedingReminders.length} subscriptions`);
      }
      return success;
    }
  } catch (error) {
    console.error(`Error sending reminders for user ${userData.userId}:`, error);
    return false;
  }
};

/**
 * Main function to send all reminders for all users
 * This should be called daily (e.g., via cron job or scheduled task)
 */
export const sendAllDailyReminders = async (): Promise<void> => {
  try {
    console.log('🕐 Starting daily reminder process...');
    
    const usersData = await getAllUsersWithSubscriptions();
    
    if (usersData.length === 0) {
      console.log('No users need reminders today');
      return;
    }

    console.log(`Found ${usersData.length} users with active subscriptions`);

    let successCount = 0;
    let errorCount = 0;

    for (const userData of usersData) {
      try {
        const success = await sendUserReminders(userData);
        if (success) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        console.error(`Error processing reminders for user ${userData.userId}:`, error);
        errorCount++;
      }
    }

    console.log(`📧 Daily reminder process completed:`);
    console.log(`✅ Successful: ${successCount}`);
    console.log(`❌ Errors: ${errorCount}`);
    console.log(`📊 Total users processed: ${usersData.length}`);

  } catch (error) {
    console.error('❌ Error in daily reminder process:', error);
  }
};

/**
 * Test function to manually trigger reminders for testing
 */
export const testRemindersForUser = async (userId: string): Promise<boolean> => {
  try {
    console.log(`🧪 Testing reminders for user ${userId}`);
    
    const { data: preferences, error: prefError } = await supabase
      .from('user_preferences')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (prefError || !preferences) {
      console.error('User preferences not found');
      return false;
    }

    const { data: subscriptions, error: subError } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true);

    if (subError || !subscriptions) {
      console.error('User subscriptions not found');
      return false;
    }

    const { data: userData, error: userError } = await supabase.auth.admin.getUserById(userId);
    
    if (userError || !userData.user) {
      console.error('User data not found');
      return false;
    }

    const userDataForReminders: ReminderData = {
      userId,
      userEmail: userData.user.email || '',
      userName: userData.user.user_metadata?.full_name || userData.user.user_metadata?.name,
      preferences,
      subscriptions,
    };

    return await sendUserReminders(userDataForReminders);
  } catch (error) {
    console.error('Error testing reminders:', error);
    return false;
  }
};
